import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { scheduleApi, ScheduleEntry, AvailabilityResult } from '@/services/api/agentApi';

interface ScheduleState {
  entries: ScheduleEntry[];
  currentAvailability: AvailabilityResult | null;
  isLoading: boolean;
  error: string | null;
}

interface ScheduleActions {
  fetchEntries: () => Promise<void>;
  fetchAvailability: () => Promise<void>;
  addEntry: (data: Partial<ScheduleEntry>) => Promise<void>;
  updateEntry: (entryId: string, data: Partial<ScheduleEntry>) => Promise<void>;
  confirmPattern: (entryId: string) => Promise<void>;
  deleteEntry: (entryId: string) => Promise<void>;
}

export const useScheduleStore = create<ScheduleState & ScheduleActions>()(
  subscribeWithSelector(
    immer((set, get) => ({
      entries: [],
      currentAvailability: null,
      isLoading: false,
      error: null,

      fetchEntries: async () => {
        set((s) => { s.isLoading = true; s.error = null; });
        try {
          const result = await scheduleApi.list();
          set((s) => { s.entries = result.data || []; s.isLoading = false; });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; s.isLoading = false; });
        }
      },

      fetchAvailability: async () => {
        try {
          const result = await scheduleApi.getAvailability();
          set((s) => { s.currentAvailability = result.data || null; });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      addEntry: async (data) => {
        try {
          await scheduleApi.create(data);
          await get().fetchEntries();
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      updateEntry: async (entryId, data) => {
        try {
          await scheduleApi.update(entryId, data);
          await get().fetchEntries();
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      confirmPattern: async (entryId) => {
        try {
          await scheduleApi.confirm(entryId);
          await get().fetchEntries();
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      deleteEntry: async (entryId) => {
        try {
          await scheduleApi.remove(entryId);
          set((s) => { s.entries = s.entries.filter(e => e.entry_id !== entryId); });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },
    }))
  )
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useScheduleStore.setState({ entries: [], currentAvailability: null, isLoading: false, error: null });
        }
      }
    );
  });
}
